import React, { useEffect, useState } from 'react';
import {
  currentDate,
  useNotification,
  getButtonSpinner,
  showBackdropLoader,
} from 'utils/commonUtils';
import { format, isValid } from 'date-fns';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  getTutoringV2Availability,
  getTutoringV2BookedSessions,
} from './services/bookNewSessionTabV2Api';
import TimeSlotsGrid from './TimeSlotCard';
import MDTypography from 'components/MDTypography';
import { GridWrapper } from 'components/Ui/styled';
import { booleanOptions, dateFormatYMD } from 'constants';
import StudentSearch from 'layouts/students/common/StudentSearch';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { formatDisplayDate, getSubjectName } from './bookNewSessionTabUtils';

const BookNewSessionTabV2 = () => {
  const { NotificationPopup, handleErrorResponse } = useNotification();
  const subjects = useSelector((state) => state?.subjectsV2);
  const tomorrow = format(
    new Date(
      new Date(currentDate).setDate(new Date(currentDate).getDate() + 1),
    ),
    dateFormatYMD,
  );

  const [resetKey, setResetKey] = useState(0);
  const [student, setStudent] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [bookedSessions, setBookedSessions] = useState([]);
  const [displayDate, setDisplayDate] = useState(tomorrow);
  const [isLoadingBackDrop, setIsLoadingBackDrop] = useState(false);

  const subjectOptions = subjects?.map((item) => ({
    id: item.subjectId,
    value: item.name,
  }));

  const defaultValue = {
    subject: '',
    studentId: '',
    date: tomorrow,
    overrideLimits: false,
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });

  const { control, reset, watch } = useFunction;

  const watchedDate = watch('date');
  const watchedSubject = watch('subject');
  const watchedOverrideLimits = watch('overrideLimits');

  const handleClearFilters = () => {
    setStudent(null);
    setTimeSlots([]);
    setBookedSessions([]);

    reset(defaultValue);

    setDisplayDate(defaultValue.date);
    setResetKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (
      student &&
      watchedSubject &&
      watchedDate &&
      isValid(new Date(watchedDate))
    ) {
      loadAllData();
    } else {
      setTimeSlots([]);
      setBookedSessions([]);
    }
  }, [student, watchedSubject, watchedDate, watchedOverrideLimits]);

  const dateInfo =
    watchedDate && isValid(new Date(watchedDate))
      ? format(new Date(watchedDate), dateFormatYMD)
      : '';

  // ───────────────────────────────────────────────
  // ✅ 1. FETCH BOOKED SESSIONS (RETURN sessionId + timeSlotId)
  // ───────────────────────────────────────────────
  const fetchBookedSessions = async () => {
    const response = await getTutoringV2BookedSessions(
      50,
      null,
      dateInfo,
      dateInfo,
      student?.studentId,
      watchedSubject,
      true,
      false,
    );

    // 👉 STORE BOTH sessionId AND timeSlotId
    const sessions =
      response?.items?.map((session) => ({
        timeSlotId: session.timeSlotId,
        sessionId: session.sessionId,
        reason: session.reason,
      })) || [];

    return sessions;
  };

  // ───────────────────────────────────────────────
  // ✅ 2. FETCH AVAILABILITY
  // ───────────────────────────────────────────────
  const fetchAvailability = async () => {
    const availability = await getTutoringV2Availability(
      student?.studentId,
      watchedSubject,
      dateInfo,
      watchedOverrideLimits,
    );

    return availability?.timeSlots || [];
  };

  // ───────────────────────────────────────────────
  // ✅ 3. LOAD BOTH IN ORDER
  // ───────────────────────────────────────────────
  const loadAllData = async () => {
    try {
      setIsLoadingBackDrop(true);

      const [ids, slots] = await Promise.all([
        fetchBookedSessions(),
        fetchAvailability(),
      ]);

      setBookedSessions(ids);
      setTimeSlots(slots);
      setDisplayDate(watchedDate);

      setIsLoadingBackDrop(false);
    } catch (error) {
      setIsLoadingBackDrop(false);
      setTimeSlots([]);
      setBookedSessions([]);
      setDisplayDate(watchedDate);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const displayDateText =
    watchedDate && isValid(new Date(watchedDate))
      ? formatDisplayDate(watchedDate)
      : '';
  const subjectName = getSubjectName(subjects, watchedSubject);
  const isReadyToLoad =
    student && watchedSubject && watchedDate && isValid(new Date(watchedDate));

  return (
    <>
      {showBackdropLoader(isLoadingBackDrop)}
      <MDBox mb={4.5} />

      <GridWrapper container spacing={2}>
        <Grid item xs={12}>
          <MDTypography variant="h5" fontWeight="medium">
            Check Availability & Book Sessions
          </MDTypography>
        </Grid>

        <Grid item xs={12}>
          <StudentSearch value={student} onSelect={setStudent} />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormInputDropdown
            name="subject"
            label="Subject"
            control={control}
            options={subjectOptions}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormInputDate
            name="date"
            label="Date"
            key={resetKey}
            value={tomorrow}
            control={control}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <FormInputDropdown
            control={control}
            name="overrideLimits"
            label="Override Normal Limits"
            options={booleanOptions}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          {getButtonSpinner(false, handleClearFilters, 'Clear Filters')}
        </Grid>

        {isReadyToLoad && (
          <>
            <Grid item xs={12}>
              <MDTypography variant="h5" fontWeight="medium">
                Available times for {subjectName} on {displayDateText}
              </MDTypography>
            </Grid>

            <Grid item xs={12}>
              <TimeSlotsGrid
                student={student}
                timeSlots={timeSlots}
                loadAllData={loadAllData}
                selectedDate={displayDate}
                isLoading={isLoadingBackDrop}
                bookedSessions={bookedSessions}
                watchedSubject={watchedSubject}
                overrideLimits={watchedOverrideLimits}
              />
            </Grid>
          </>
        )}
      </GridWrapper>

      <NotificationPopup />
    </>
  );
};

export default BookNewSessionTabV2;
